import React, { forwardRef, useState, useEffect } from "react";
import { Breadcrumb } from "components";
import { translate } from "react-switch-lang";
import { AddBox, ArrowDownward } from "@material-ui/icons";
import MaterialTable, { MTableToolbar } from "material-table";
import Check from "@material-ui/icons/Check";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Clear from "@material-ui/icons/Clear";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import FilterList from "@material-ui/icons/FilterList";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import Remove from "@material-ui/icons/Remove";
import SaveAlt from "@material-ui/icons/SaveAlt";
import Search from "@material-ui/icons/Search";
import ViewColumn from "@material-ui/icons/ViewColumn";
import CircularProgress from "@material-ui/core/CircularProgress";
import PrintIcon from "@material-ui/icons/Print";
import CloudDownloadOutlinedIcon from "@material-ui/icons/CloudDownloadOutlined";
import {
  addRegion,
  getRegions,
  uploadRegionFile,
} from "service/manageRegionService";
import Swal from "sweetalert2";
import TableToolbar from "components/tableToolbar";
import ImportDataModal from "components/importDataModal";

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  print: forwardRef((props, ref) => <PrintIcon {...props} ref={ref} />),
  DownloadTable: forwardRef((props, ref) => (
    <CloudDownloadOutlinedIcon
      style={{ fontSize: "larger", fontWeight: "bolder" }}
      {...props}
      ref={ref}
    />
  )),
};

const ManageRegion = (props) => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [showImportDataModal, setShowImportDataModal] = useState(false);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);

  useEffect(() => {
    getData();
  }, []);

  const getData = async (loading = true) => {
    setLoader(loading);
    try {
      const res = await getRegions();
      console.log("Raw regions response:", res);

      if (res && res.data) {
        let regions = res.data;

        // Validate: Ensure array of objects
        if (Array.isArray(regions)) {
          const validData = regions.filter(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              item.region_code &&
              item.region_name
          );
          setData(validData);
        } else {
          console.warn("Invalid regions data received", regions);
          setData([]); // Fallback to empty data
        }
      } else {
        console.warn("No data property in response", res);
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
      Swal.fire({
        icon: "error",
        title: error.message || "Error occurred while fetching regions",
      });
    } finally {
      setLoader(false);
    }
  };

  const onAddData = (newData = {}, isUpdate) => {
    return new Promise((resolve, reject) => {
      addRegion({
        ...newData,
        region_code: newData.region_code.toString(),
        isUpdate,
      })
        .then((res) => {
          getData(false).finally(() => {
            resolve(true);
          });
          if (res.status !== 200) {
            Swal.fire({
              icon: "error",
              title: res.message || "Error occured",
            });
            reject(res.error);
          } else {
            Swal.fire({
              icon: "success",
              title: `Region ${isUpdate ? "Updated" : "Added"} Successfully`,
            });
          }
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            title: error.message || "Error occured",
          });
          reject(error);
        });
    });
  };

  const uploadFile = (file) => {
    const formData = new FormData();
    formData.append("region", file);

    setUploadFileLoading(true);
    console.log("manageRegion uploadFile", file, formData);
    uploadRegionFile(formData)
      .then((res) => {
        if (res.status === 200) {
          getData();
          setShowImportDataModal(false);
          Swal.fire({
            icon: "success",
            title: props.t("Data Imported"),
          });
        } else {
          Swal.fire({
            icon: "error",
            title: res.message || "Error occured",
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: error.message || "Error occured",
        });
      })
      .finally(() => {
        setUploadFileLoading(false);
      });
  };

  return (
    <>
      <Breadcrumb title={props.t("Manage Region")} />
      <div className="app-wrapper">
        <div>
          {loader ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                height:
                  props.width >= 1200
                    ? "calc(100vh - 259px)"
                    : "calc(100vh - 238px)",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div className="animated slideInUpTiny animation-duration-3">
              <MaterialTable
                icons={tableIcons}
                columns={[
                  { title: props.t("Region Name"), field: "region_name" },
                  {
                    title: props.t("Code"),
                    field: "region_code",
                    editable: "onAdd",
                  },
                ]}
                data={data}
                options={{
                  actionsColumnIndex: -1,
                  addRowPosition: "first",
                  showTitle: false,
                  filtering: true,
                  sorting: true,
                }}
                editable={{
                  onRowAdd: (data) => onAddData(data, false),
                  onRowUpdate: (data) => onAddData(data, true),
                  onRowDelete: (oldData) =>
                    new Promise((resolve, reject) => {
                      setTimeout(() => {
                        const dataDelete = [...data];
                        const index = oldData.tableData.id;
                        dataDelete.splice(index, 1);
                        setData([...dataDelete]);

                        resolve();
                      }, 1000);
                    }),
                }}
                localization={{
                  header: {
                    actions: props.t("Action"),
                  },
                }}
                components={{
                  Toolbar: (propsToolbar) => (
                    <TableToolbar
                      onClickImport={() => setShowImportDataModal(true)}
                      {...propsToolbar}
                      t={props.t}
                    />
                  ),
                }}
              />
            </div>
          )}
        </div>
      </div>
      <ImportDataModal
        onImport={uploadFile}
        loading={uploadFileLoading}
        show={showImportDataModal}
        setShow={setShowImportDataModal}
      />
    </>
  );
};

export default translate(ManageRegion);
